import { ShimmerText } from '@/components/ui/shimmer-text';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DEFAULT_POSITIONS_MOCK_ROWS, usePageSkeletonPreset } from '@/lib/page-skeleton-presets';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/**
 * Structure-preserving skeletons. Static chrome (headers, labels, tabs) renders
 * immediately; loading indicators sit at the deepest leaf (number bars, cells).
 */

function CellPlaceholder({
  children,
  mode,
  className,
}: {
  children?: ReactNode;
  mode: 'blur' | 'skeleton';
  className?: string;
}) {
  if (mode === 'skeleton') {
    return <Skeleton className={cn('h-4 max-w-[8rem]', className)} aria-hidden />;
  }
  return <ShimmerText className={className}>{children ?? '\u00A0'}</ShimmerText>;
}

/** DataTable shell: sharp column headers; body cells blurred or skeleton. */
export function TableSkeleton({
  headers,
  rows: mockRows,
  rowCount = 6,
  columns = 4,
  mode = 'blur',
  className,
  columnClassNames,
}: {
  /** Real i18n column titles — always sharp. */
  headers?: string[];
  /** Mock cell values for blur mode. */
  rows?: string[][];
  rowCount?: number;
  columns?: number;
  mode?: 'blur' | 'skeleton';
  className?: string;
  columnClassNames?: string[];
}) {
  const resolvedHeaders = headers ?? Array.from({ length: columns }, (_, i) => `Column ${i + 1}`);
  const colCount = resolvedHeaders.length;
  const resolvedRows = mockRows ?? (mode === 'blur' ? DEFAULT_POSITIONS_MOCK_ROWS : undefined);

  const bodyRows: (string[] | undefined)[] = [];
  for (let r = 0; r < rowCount; r += 1) {
    if (resolvedRows?.length) {
      bodyRows.push(resolvedRows[r % resolvedRows.length]);
    } else {
      bodyRows.push(undefined);
    }
  }

  return (
    <div
      className={cn('flex flex-col', className)}
      aria-busy={mode === 'skeleton' ? true : undefined}
    >
      <Table containerClassName='max-w-full overflow-x-auto overscroll-x-contain'>
        <TableHeader className='bg-muted sticky top-0 z-20 border-b shadow-xs'>
          <TableRow className='hover:bg-transparent'>
            {resolvedHeaders.map((header, i) => (
              <TableHead
                key={`${header}-${i}`}
                className={cn(columnClassNames?.[i], i > 0 && i >= colCount - 2 && 'text-right')}
              >
                {header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody aria-hidden={mode === 'blur' ? true : undefined}>
          {bodyRows.map((row, r) => (
            <TableRow key={r} className='hover:bg-transparent'>
              {resolvedHeaders.map((_, c) => (
                <TableCell key={c} className={cn(c >= colCount - 2 && 'text-right tabular-nums')}>
                  <CellPlaceholder mode={mode}>{row?.[c]}</CellPlaceholder>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Silhouette of a chart panel: a block of the chart's height. */
export function ChartSkeleton({
  height = 320,
  className,
}: {
  height?: number;
  className?: string;
}) {
  return <Skeleton className={cn('w-full rounded-xl', className)} style={{ height }} aria-hidden />;
}

/**
 * Lazy-route fallback: real hero labels + tabs + table headers immediately;
 * only hero numbers and table cells indicate loading.
 */
export function PageSkeleton() {
  const { hero, tabs, tableHeaders, tableRows, content } = usePageSkeletonPreset();

  if (!hero && !tabs) {
    return null;
  }

  return (
    <div className='mx-auto flex w-full max-w-6xl flex-col gap-4' aria-busy='true'>
      {hero}
      {tabs}
      {content ??
        (tableHeaders.length > 0 ? (
          <TableSkeleton headers={tableHeaders} rows={tableRows} mode='blur' />
        ) : null)}
    </div>
  );
}
